// La COLUMNA DE RESULTADOS de un modo con ruta propia —
// ADR-2026-09-08-animar-y-carrusel-generan-en-la-columna.
//
// Es lo que `InterfazV2` hace para Automática / Modelo / Fotos de producto / Catálogo
// (crear la tanda, cerrarla con el preload, dibujar `PanelTandaCreaciones` donde estaba
// la muestra), empaquetado para los modos que NO viven adentro de `InterfazV2` y por eso
// no tenían dónde aterrizar: Animar imagen y Carrusel se llevaban la pantalla con el
// loader full-screen y terminaban en `/redes-sociales/imagenes-generadas`, o sea en una
// pantalla por la que el usuario no había pasado y desde la que volver al modo es
// empezar de nuevo (lo mismo que arregló
// ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla para los otros).
//
// Qué da:
//   • `columna`: el `PanelTandaCreaciones` ya envuelto en SU scroller, listo para
//     ponerlo como `muestra` de `ShellModoRedisenado`. `null` cuando no hay nada que
//     mostrar — ahí el modo se queda con su muestra de siempre.
//   • `lanzarTanda`: recibe la promesa del pedido YA EN VUELO y se ocupa del resto
//     (recuadros, preload, cierre, error). No espera: la función que la llama vuelve
//     enseguida y el usuario se queda en el modo.
//   • `puedeCrear`: el tope de tandas en curso (ADR-2026-09-06-tope-de-tres-tandas-en-curso),
//     para preguntarlo ANTES de disparar el pedido.
//
// El estado vive en `generacionesStore` (fuera de React), así que la promesa cierra la
// tanda aunque el modo ya se haya desmontado, y las tandas de otros modos se ven acá
// igual que las de éste (ADR-2026-09-06-las-tandas-se-ven-en-todos-los-modos).

import React from 'react';
import { GenerateAdError } from '../../../../lib/generateAdRequest';
import { useVerificacionCalidad } from '../../../../hooks/useFuncionesAvanzadas';
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from '../../../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../../../utils/preloadVideosDeTanda';
import { sonarListo } from '../../../../utils/sonarListo';
import { PanelTandaCreaciones } from '../../components/PanelTandaCreaciones';
import { COLUMNA_RESULTADOS_AIRE_INTERNO } from '../../components/layoutModo';
import { useTandasPanel } from '../../useGeneracionesEnCurso';
import { MAX_TANDAS_EN_CURSO } from '../../generacionesEnCurso';

/** El aviso de que el tope de generaciones en curso está lleno, cuando el pedido YA
 *  salió: no se frenó nada, así que "esperá" mentiría. Mismo copy que `InterfazV2`, y el
 *  número sale de la constante: escrito a mano se desfasa al mover el tope. */
const AVISO_TOPE_YA_LANZADO =
  `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso: ésta va a aparecer en Creaciones cuando termine.`;

/** La forma de TODAS las cards de la columna (`RATIO_CARD` en `PanelTandaCreaciones`):
 *  el `ratio` de la tanda ya no decide nada de lo que se dibuja
 *  (ADR-2026-09-06-la-columna-de-resultados-va-toda-en-4-5), así que el modo que no
 *  tiene un formato propio que declarar manda éste. */
const RATIO_POR_DEFECTO = 0.8;

export interface LanzarTandaParams {
  /** Cuántos resultados van a salir: la cantidad de recuadros. */
  total: number;
  /** Proporción de lo que va a salir. Por defecto 4:5 — ver `RATIO_POR_DEFECTO`. */
  ratio?: number;
  /** Nombre del producto para rotular las cards; vacío = sólo la hora. */
  producto?: string;
  /** El resultado es un video: los recuadros dicen "Generando video…" y las cards
   *  terminadas llevan el play. */
  esVideo?: boolean;
  /** Créditos que cobra, para el guard del tope. Por defecto `total` — que en un video
   *  mentiría: ahí cobra la duración (ADR-2026-09-06-tope-de-tres-tandas-en-curso). */
  creditos?: number;
  /** Estimación de cuánto tarda (ms), para la barra de los recuadros. */
  duracionEstimadaMs?: number;
  /** El pedido, ya disparado: resuelve con los ids de `imagenes_generadas`. */
  resultado: Promise<string[]>;
  /**
   * El `execution_id` de la corrida, en cuanto el backend acepta el pedido.
   *
   * Sin esto la tanda vive SÓLO en la memoria de la pestaña: si el usuario recarga o se
   * va, se pierde el seguimiento de una corrida que el backend ya aceptó —y que va a
   * terminar y cobrar igual—
   * (ADR-2026-09-25-revision-greptile-la-tanda-aceptada-se-puede-reanudar). Con el id
   * anotado, el reanudador del shell la retoma al volver.
   *
   * Es una promesa porque el id llega DESPUÉS de lanzar: la tanda se dibuja al tocar
   * Generar y el 202 vuelve unos instantes más tarde. Opcional: quien no lo tenga —una
   * generación delegada desde el visor— sigue como antes.
   */
  executionId?: Promise<string | null>;
  /** Qué decirle al usuario si rechaza sin un mensaje propio. */
  errorGenerico: string;
}

export interface ColumnaResultadosApi {
  /** La columna lista para usar como `muestra`, o `null` si no hay resultados. */
  columna: React.ReactNode | null;
  /** `true` cuando `columna` no es `null` — para que el modo decida el layout sin
   *  preguntarle al nodo. */
  hayColumna: boolean;
  /** El visor de una creación está abierto: el shell tiene que cederle el z-index. */
  visorAbierto: boolean;
  /** Arranca la tanda con la promesa del pedido. `false` si no había lugar. */
  lanzarTanda: (p: LanzarTandaParams) => boolean;
  /** ¿Entra otra tanda? Preguntarlo ANTES de disparar el pedido. */
  puedeCrear: () => boolean;
  /**
   * La columna como HISTORIAL del modo: las tandas en curso más lo que el modo generó
   * antes, aunque la sesión no haya generado nada. Sólo si se pidió con `historial`; si
   * no, `null`. Lo usa la pestaña Historial de Recrear video
   * (ADR-2026-10-09-recrear-video-adentro-de-recrear).
   */
  historial: React.ReactNode | null;
}

export function useColumnaResultadosModo({
  companyId,
  brandId = null,
  modo,
  onError,
  onLimitePlan,
  historial,
}: {
  companyId: string | null;
  brandId?: string | null;
  /** El `kind` del modo: rotula las tandas ajenas y deja sin chip a las propias. */
  modo: string;
  onError: (mensaje: string) => void;
  /** El backend cobró de menos: el modo abre su `SubscriptionLimitModal`. */
  onLimitePlan: () => void;
  /** Con esto el hook arma además `historial`: lo de ese `modo_utilizado`, sólo videos si se pide. */
  historial?: { modoUtilizado: string; soloVideos?: boolean };
}): ColumnaResultadosApi {
  const {
    tandasPanel,
    hayResultados,
    columnaMuestraTandas,
    recargarToken,
    pedirRecarga,
    crearTanda,
    registrarExecutionIds,
    cerrarTanda,
    fallarTanda,
    puedeCrear,
  } = useTandasPanel(companyId, modo);
  const verificacionActiva = useVerificacionCalidad();
  const [visorAbierto, setVisorAbierto] = React.useState(false);

  const lanzarTanda = React.useCallback(
    (p: LanzarTandaParams): boolean => {
      if (!companyId) return false;
      const esVideo = p.esVideo ?? false;
      // El tope, con el pedido ya en vuelo: no se pierde —cuando resuelva se pide
      // recarga y entra por las grillas—, sólo no ocupa un lugar en la columna.
      if (!puedeCrear()) {
        onError(AVISO_TOPE_YA_LANZADO);
        p.resultado.then(
          () => pedirRecarga(),
          () => {
            /* sin tanda no hay recuadros que tirar */
          },
        );
        return false;
      }
      const idTanda = crearTanda({
        total: p.total,
        ratio: p.ratio ?? RATIO_POR_DEFECTO,
        producto: p.producto,
        esVideo,
        modo,
        creditos: p.creditos,
        duracionEstimadaMs: p.duracionEstimadaMs,
        // Sin `execution_id` conocido: estas tandas viven en memoria de la pestaña y no
        // se reanudan tras recargar, igual que las delegadas desde el visor
        // (ver `generacionDelegada`).
        executionIds: [],
      });
      if (!idTanda) return false;
      // Apenas el backend acepta, el id queda anotado en la tanda: es lo que deja que el
      // reanudador del shell la retome si el usuario recarga antes de que termine.
      p.executionId
        ?.then((executionId) => {
          if (executionId) registrarExecutionIds(idTanda, [executionId]);
        })
        .catch(() => {
          /* si el pedido falla lo cuenta `resultado`; acá no hay nada que anotar */
        });
      p.resultado
        .then(async (ids) => {
          let preloaded: PreloadedImage[] = [];
          try {
            preloaded = esVideo
              ? await preloadVideosDeTanda(ids, companyId)
              : await preloadGeneratedImages(ids, companyId);
          } catch (e) {
            // Sin preload la tanda cierra vacía y `cerrarTanda` pide recarga: las filas
            // ya están en la base.
            console.warn('[useColumnaResultadosModo] preload falló', e);
          }
          cerrarTanda(idTanda, preloaded, { esVideo });
          // El "listo" — mismo hueco que tenían las tandas delegadas de `InterfazV2`
          // (ADR-2026-09-20-las-tandas-delegadas-tambien-suenan-al-terminar).
          sonarListo();
        })
        .catch((err: unknown) => {
          console.error('[useColumnaResultadosModo] la generación falló', err);
          if (err instanceof GenerateAdError && err.errorType === 'billing') {
            onLimitePlan();
          } else if (err instanceof GenerateAdError && err.isActionable) {
            onError(err.message);
          } else {
            onError(p.errorGenerico);
          }
          fallarTanda(idTanda);
        });
      return true;
    },
    [
      companyId,
      modo,
      crearTanda,
      registrarExecutionIds,
      cerrarTanda,
      fallarTanda,
      puedeCrear,
      pedirRecarga,
      onError,
      onLimitePlan,
    ],
  );

  /*
   * Con resultados en la sesión, la columna es de los resultados: no hay paso ni modo que
   * le devuelva la portada — ADR-2026-09-08-la-columna-de-resultados-no-vuelve-a-la-portada.
   * La muestra del modo se ve mientras la sesión no generó nada, que es cuando de verdad
   * hace falta que la columna explique el modo.
   */
  const hayColumna = columnaMuestraTandas && hayResultados;

  // El panel, con lo que cambia entre la columna de la sesión y el historial del modo.
  const panel = (extra: { filtro?: 'todo' | 'videos'; modoUtilizado?: string } = {}) => (
    <PanelTandaCreaciones
      tandas={tandasPanel}
      modoActual={modo}
      recargarToken={recargarToken}
      companyId={companyId}
      brandId={brandId}
      onVisorChange={setVisorAbierto}
      // Carrusel / Animar pedidos desde el visor de una creación aterrizan en ESTA
      // columna, igual que en los modos de `InterfazV2`
      // (ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna).
      onGeneracionDelegada={(g) =>
        lanzarTanda({
          total: g.total,
          ratio: g.ratio,
          producto: g.producto,
          esVideo: g.esVideo,
          creditos: g.creditos,
          duracionEstimadaMs: g.duracionEstimadaMs,
          resultado: g.resultado,
          errorGenerico:
            g.tipo === 'pasar-a-1080p'
              ? 'No se pudo pasar el video a 1080p. Intentá nuevamente.'
              : g.tipo === 'animar'
              ? 'No se pudo generar el video. Intentá nuevamente.'
              : g.tipo === 'regenerar'
                ? 'No se pudo regenerar la imagen. Intentá nuevamente.'
                : 'No se pudo generar nuevos ángulos. Intentá nuevamente.',
        })
      }
      verificacionActiva={verificacionActiva}
      {...extra}
    />
  );

  const columna = hayColumna ? (
    <div
      className={`flex-1 min-h-0 overflow-y-auto ${COLUMNA_RESULTADOS_AIRE_INTERNO}`}
    >
      {panel()}
    </div>
  ) : null;

  const historialDelModo = historial && companyId ? (
    <div
      className={`flex-1 min-h-0 overflow-y-auto ${COLUMNA_RESULTADOS_AIRE_INTERNO}`}
    >
      {panel({
        modoUtilizado: historial.modoUtilizado,
        filtro: historial.soloVideos ? 'videos' : 'todo',
      })}
    </div>
  ) : null;

  return { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear, historial: historialDelModo };
}

export default useColumnaResultadosModo;
